import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
// Note: `title` / `subtitle` tokens were removed — the page's PageHero + PageBlurb
// sections already supply the heading for this page.
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useColor4 } from '../hooks/useColor4';

// ============================================================================
// Static slot group definitions for provider logos & CTAs
// ============================================================================
// Default logo URLs use Clearbit's public logo API (https://clearbit.com/logo),
// which returns a transparent PNG for a given domain. Each is wrapped in an
// editable image slot group so property owners can swap in a licensed asset.

const card1LogoDef: SlotGroupDefinition = {
  label: 'Flex Logo',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: 'https://logo.clearbit.com/getflex.com' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Flex logo' },
  },
};

const card1CtaDef: SlotGroupDefinition = {
  label: 'Split Payments CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://getflex.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Go to Flex' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const card2LogoDef: SlotGroupDefinition = {
  label: 'Obligo Logo',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: 'https://logo.clearbit.com/myobligo.com' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Obligo logo' },
  },
};

const card2CtaDef: SlotGroupDefinition = {
  label: 'Flexible Deposits CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.myobligo.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Go to Obligo' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const card3LogoDef: SlotGroupDefinition = {
  label: 'Beagle Insurance Logo',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: 'https://logo.clearbit.com/beagleinsurance.com' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Beagle Insurance logo' },
  },
};

const card3CtaDef: SlotGroupDefinition = {
  label: 'Insurance CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.beagleinsurance.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Go to Beagle Insurance' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const card4LogoDef: SlotGroupDefinition = {
  label: 'RentCafe Logo',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: 'https://logo.clearbit.com/rentcafe.com' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Rewards by RentCafe logo' },
  },
};

const card4CtaDef: SlotGroupDefinition = {
  label: 'Rewards CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.rentcafe.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Go to Rewards by RentCafe' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const card5LogoDef: SlotGroupDefinition = {
  label: 'theGuarantors Logo',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: 'https://logo.clearbit.com/theguarantors.com' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'theGuarantors logo' },
  },
};

const card5CtaDef: SlotGroupDefinition = {
  label: 'Lease Guarantee CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.theguarantors.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Go to theGuarantors' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const card6LogoDef: SlotGroupDefinition = {
  label: 'Pineapple Logo',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: 'https://logo.clearbit.com/getpineapple.com' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Pineapple logo' },
  },
};

const card6CtaDef: SlotGroupDefinition = {
  label: 'Self-Guided Tours CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.getpineapple.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Go to Pineapple' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

function ArrowIcon() {
  return (
    <svg className="relative z-10 w-4 h-4 shrink-0 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FlexibleLivingPrograms() {
  const card1Title = useContentToken('flexiblelivingprograms.card1_title');
  const card2Title = useContentToken('flexiblelivingprograms.card2_title');
  const card3Title = useContentToken('flexiblelivingprograms.card3_title');
  const card4Title = useContentToken('flexiblelivingprograms.card4_title');
  const card5Title = useContentToken('flexiblelivingprograms.card5_title');
  const card6Title = useContentToken('flexiblelivingprograms.card6_title');

  const card1Logo = useSlotGroup('flexiblelivingprograms.card1_logo', card1LogoDef);
  const card2Logo = useSlotGroup('flexiblelivingprograms.card2_logo', card2LogoDef);
  const card3Logo = useSlotGroup('flexiblelivingprograms.card3_logo', card3LogoDef);
  const card4Logo = useSlotGroup('flexiblelivingprograms.card4_logo', card4LogoDef);
  const card5Logo = useSlotGroup('flexiblelivingprograms.card5_logo', card5LogoDef);
  const card6Logo = useSlotGroup('flexiblelivingprograms.card6_logo', card6LogoDef);

  const card1Cta = useSlotGroup('flexiblelivingprograms.card1_cta', card1CtaDef);
  const card2Cta = useSlotGroup('flexiblelivingprograms.card2_cta', card2CtaDef);
  const card3Cta = useSlotGroup('flexiblelivingprograms.card3_cta', card3CtaDef);
  const card4Cta = useSlotGroup('flexiblelivingprograms.card4_cta', card4CtaDef);
  const card5Cta = useSlotGroup('flexiblelivingprograms.card5_cta', card5CtaDef);
  const card6Cta = useSlotGroup('flexiblelivingprograms.card6_cta', card6CtaDef);
  const color4 = useColor4();

  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });
  const disclaimerRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pt-4 pb-6 md:pt-6 md:pb-10 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Card 1 — Split Payments / Flex */}
          <article data-reveal className="reveal-fade-up flp-card bg-white border border-[var(--vibe-color-border)] overflow-hidden flex flex-col">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card1_logo" definition={card1LogoDef} className="flp-logo-banner">
              {card1Logo.image ? (
                <VibesitesImage
                  url={card1Logo.image}
                  alt={card1Logo.alt || 'Flex logo'}
                  sizes="(min-width: 1024px) 320px, (min-width: 768px) 50vw, 90vw"
                  className="flp-logo-img"
                />
              ) : (
                <span className="font-heading text-2xl sm:text-3xl font-bold text-[var(--vibe-color-primary)]">Flex</span>
              )}
            </EditableSlotGroup>
            <div className="flp-card-body p-6 sm:p-8 flex flex-col flex-grow">
            <EditableToken
              path="flexiblelivingprograms.card1_title"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-semibold text-[var(--vibe-color-text)] mb-3 leading-tight"
            >
              {card1Title}
            </EditableToken>
            <RichTextContent
              path="flexiblelivingprograms.card1_description"
              defaultValue="<p>Divide rent into two payments aligned with your pay schedule. Stay on top of bills without stretching your budget.</p>"
              as="div"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed flex-grow mb-6 [&_p]:mb-2 [&_p:last-child]:mb-0"
            />
            <EditableSlotGroup groupPath="flexiblelivingprograms.card1_cta" definition={card1CtaDef}>
              <a
                href={card1Cta.url || 'https://getflex.com/'}
                target={card1Cta._target || '_blank'}
                rel="noopener noreferrer"
                style={color4?.primaryOverride}
                className="flp-cta group/cta relative inline-flex items-center justify-center gap-2 px-6 py-4 text-xs sm:text-sm font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{card1Cta.label || 'Go to Flex'}</span>
                <ArrowIcon />
              </a>
            </EditableSlotGroup>
            </div>
          </article>

          {/* Card 2 — Flexible Deposits / Obligo */}
          <article data-reveal className="reveal-fade-up flp-card bg-white border border-[var(--vibe-color-border)] overflow-hidden flex flex-col">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card2_logo" definition={card2LogoDef} className="flp-logo-banner">
              {card2Logo.image ? (
                <VibesitesImage
                  url={card2Logo.image}
                  alt={card2Logo.alt || 'Obligo logo'}
                  sizes="(min-width: 1024px) 320px, (min-width: 768px) 50vw, 90vw"
                  className="flp-logo-img"
                />
              ) : (
                <span className="font-heading text-2xl sm:text-3xl font-bold text-[var(--vibe-color-primary)]">Obligo</span>
              )}
            </EditableSlotGroup>
            <div className="flp-card-body p-6 sm:p-8 flex flex-col flex-grow">
            <EditableToken
              path="flexiblelivingprograms.card2_title"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-semibold text-[var(--vibe-color-text)] mb-3 leading-tight"
            >
              {card2Title}
            </EditableToken>
            <RichTextContent
              path="flexiblelivingprograms.card2_description"
              defaultValue="<p>Reduce upfront move-in costs with flexible deposit options — including no deposit, Reduced Deposit&trade;, installments, or a traditional full deposit.</p>"
              as="div"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed flex-grow mb-6 [&_p]:mb-2 [&_p:last-child]:mb-0"
            />
            <EditableSlotGroup groupPath="flexiblelivingprograms.card2_cta" definition={card2CtaDef}>
              <a
                href={card2Cta.url || 'https://www.myobligo.com/'}
                target={card2Cta._target || '_blank'}
                rel="noopener noreferrer"
                style={color4?.primaryOverride}
                className="flp-cta group/cta relative inline-flex items-center justify-center gap-2 px-6 py-4 text-xs sm:text-sm font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{card2Cta.label || 'Go to Obligo'}</span>
                <ArrowIcon />
              </a>
            </EditableSlotGroup>
            </div>
          </article>

          {/* Card 3 — Insurance / Beagle */}
          <article data-reveal className="reveal-fade-up flp-card bg-white border border-[var(--vibe-color-border)] overflow-hidden flex flex-col">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card3_logo" definition={card3LogoDef} className="flp-logo-banner">
              {card3Logo.image ? (
                <VibesitesImage
                  url={card3Logo.image}
                  alt={card3Logo.alt || 'Beagle Insurance logo'}
                  sizes="(min-width: 1024px) 320px, (min-width: 768px) 50vw, 90vw"
                  className="flp-logo-img"
                />
              ) : (
                <span className="font-heading text-2xl sm:text-3xl font-bold text-[var(--vibe-color-primary)]">Beagle</span>
              )}
            </EditableSlotGroup>
            <div className="flp-card-body p-6 sm:p-8 flex flex-col flex-grow">
            <EditableToken
              path="flexiblelivingprograms.card3_title"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-semibold text-[var(--vibe-color-text)] mb-3 leading-tight"
            >
              {card3Title}
            </EditableToken>
            <RichTextContent
              path="flexiblelivingprograms.card3_description"
              defaultValue="<p>Get a quick quote and the liability coverage you need — with optional personal property protection for peace of mind.</p>"
              as="div"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed flex-grow mb-6 [&_p]:mb-2 [&_p:last-child]:mb-0"
            />
            <EditableSlotGroup groupPath="flexiblelivingprograms.card3_cta" definition={card3CtaDef}>
              <a
                href={card3Cta.url || 'https://www.beagleinsurance.com/'}
                target={card3Cta._target || '_blank'}
                rel="noopener noreferrer"
                style={color4?.primaryOverride}
                className="flp-cta group/cta relative inline-flex items-center justify-center gap-2 px-6 py-4 text-xs sm:text-sm font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{card3Cta.label || 'Go to Beagle Insurance'}</span>
                <ArrowIcon />
              </a>
            </EditableSlotGroup>
            </div>
          </article>

          {/* Card 4 — Rewards / RentCafe */}
          <article data-reveal className="reveal-fade-up flp-card bg-white border border-[var(--vibe-color-border)] overflow-hidden flex flex-col">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card4_logo" definition={card4LogoDef} className="flp-logo-banner">
              {card4Logo.image ? (
                <VibesitesImage
                  url={card4Logo.image}
                  alt={card4Logo.alt || 'Rewards by RentCafe logo'}
                  sizes="(min-width: 1024px) 320px, (min-width: 768px) 50vw, 90vw"
                  className="flp-logo-img"
                />
              ) : (
                <span className="font-heading text-2xl sm:text-3xl font-bold text-[var(--vibe-color-primary)]">RentCafe</span>
              )}
            </EditableSlotGroup>
            <div className="flp-card-body p-6 sm:p-8 flex flex-col flex-grow">
            <EditableToken
              path="flexiblelivingprograms.card4_title"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-semibold text-[var(--vibe-color-text)] mb-3 leading-tight"
            >
              {card4Title}
            </EditableToken>
            <RichTextContent
              path="flexiblelivingprograms.card4_description"
              defaultValue="<p>Get rewarded for things you already pay for. Earn points for insurance, redeem them for gift cards and dining, and build credit along the way.</p>"
              as="div"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed flex-grow mb-6 [&_p]:mb-2 [&_p:last-child]:mb-0"
            />
            <EditableSlotGroup groupPath="flexiblelivingprograms.card4_cta" definition={card4CtaDef}>
              <a
                href={card4Cta.url || 'https://www.rentcafe.com/'}
                target={card4Cta._target || '_blank'}
                rel="noopener noreferrer"
                style={color4?.primaryOverride}
                className="flp-cta group/cta relative inline-flex items-center justify-center gap-2 px-6 py-4 text-xs sm:text-sm font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{card4Cta.label || 'Go to Rewards by RentCafe'}</span>
                <ArrowIcon />
              </a>
            </EditableSlotGroup>
            </div>
          </article>

          {/* Card 5 — Lease Guarantee / theGuarantors */}
          <article data-reveal className="reveal-fade-up flp-card bg-white border border-[var(--vibe-color-border)] overflow-hidden flex flex-col">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card5_logo" definition={card5LogoDef} className="flp-logo-banner">
              {card5Logo.image ? (
                <VibesitesImage
                  url={card5Logo.image}
                  alt={card5Logo.alt || 'theGuarantors logo'}
                  sizes="(min-width: 1024px) 320px, (min-width: 768px) 50vw, 90vw"
                  className="flp-logo-img"
                />
              ) : (
                <span className="font-heading text-2xl sm:text-3xl font-bold text-[var(--vibe-color-primary)]">theGuarantors</span>
              )}
            </EditableSlotGroup>
            <div className="flp-card-body p-6 sm:p-8 flex flex-col flex-grow">
            <EditableToken
              path="flexiblelivingprograms.card5_title"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-semibold text-[var(--vibe-color-text)] mb-3 leading-tight"
            >
              {card5Title}
            </EditableToken>
            <RichTextContent
              path="flexiblelivingprograms.card5_description"
              defaultValue="<p>Great for the self-employed, first-time renters, or anyone who doesn't quite meet standard requirements. A lease guarantor helps you move in faster.</p>"
              as="div"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed flex-grow mb-6 [&_p]:mb-2 [&_p:last-child]:mb-0"
            />
            <EditableSlotGroup groupPath="flexiblelivingprograms.card5_cta" definition={card5CtaDef}>
              <a
                href={card5Cta.url || 'https://www.theguarantors.com/'}
                target={card5Cta._target || '_blank'}
                rel="noopener noreferrer"
                style={color4?.primaryOverride}
                className="flp-cta group/cta relative inline-flex items-center justify-center gap-2 px-6 py-4 text-xs sm:text-sm font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{card5Cta.label || 'Go to theGuarantors'}</span>
                <ArrowIcon />
              </a>
            </EditableSlotGroup>
            </div>
          </article>

          {/* Card 6 — Self-Guided Tours / Pineapple */}
          <article data-reveal className="reveal-fade-up flp-card bg-white border border-[var(--vibe-color-border)] overflow-hidden flex flex-col">
            <EditableSlotGroup groupPath="flexiblelivingprograms.card6_logo" definition={card6LogoDef} className="flp-logo-banner">
              {card6Logo.image ? (
                <VibesitesImage
                  url={card6Logo.image}
                  alt={card6Logo.alt || 'Pineapple logo'}
                  sizes="(min-width: 1024px) 320px, (min-width: 768px) 50vw, 90vw"
                  className="flp-logo-img"
                />
              ) : (
                <span className="font-heading text-2xl sm:text-3xl font-bold text-[var(--vibe-color-primary)]">Pineapple</span>
              )}
            </EditableSlotGroup>
            <div className="flp-card-body p-6 sm:p-8 flex flex-col flex-grow">
            <EditableToken
              path="flexiblelivingprograms.card6_title"
              as="h3"
              className="font-heading text-xl sm:text-2xl font-semibold text-[var(--vibe-color-text)] mb-3 leading-tight"
            >
              {card6Title}
            </EditableToken>
            <RichTextContent
              path="flexiblelivingprograms.card6_description"
              defaultValue="<p>Tour on your schedule with Pineapple's self-guided touring platform. Available evenings and weekends so you can see your future home when it works for you.</p>"
              as="div"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed flex-grow mb-6 [&_p]:mb-2 [&_p:last-child]:mb-0"
            />
            <EditableSlotGroup groupPath="flexiblelivingprograms.card6_cta" definition={card6CtaDef}>
              <a
                href={card6Cta.url || 'https://www.getpineapple.com/'}
                target={card6Cta._target || '_blank'}
                rel="noopener noreferrer"
                style={color4?.primaryOverride}
                className="flp-cta group/cta relative inline-flex items-center justify-center gap-2 px-6 py-4 text-xs sm:text-sm font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{card6Cta.label || 'Go to Pineapple'}</span>
                <ArrowIcon />
              </a>
            </EditableSlotGroup>
            </div>
          </article>
        </div>

        <div ref={disclaimerRef} className="reveal-fade-up mt-16 max-w-4xl mx-auto">
          <RichTextContent
            path="flexiblelivingprograms.disclaimer"
            label="Disclaimer"
            defaultValue="<p>These programs are provided by independent third-party service providers and are not owned, operated, endorsed, or guaranteed by the property owner or its property management company. Participation is optional and subject to each provider's terms, eligibility, and fees.</p>"
            as="div"
            className="font-body text-xs sm:text-sm text-[var(--vibe-color-text-muted)]/80 italic leading-relaxed text-center [text-wrap:balance] [&_p]:m-0"
          />
        </div>
      </div>

      <style>{`
        .flp-card {
          border-radius: 4px;
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .flp-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 30px -10px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 50%, var(--vibe-color-border));
        }
        .flp-logo-banner {
          width: 100%;
          height: 180px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          background: #ffffff;
          border-bottom: 1px solid var(--vibe-color-border);
        }
        .flp-logo-img {
          position: absolute;
          top: 28px;
          left: 32px;
          width: calc(100% - 64px);
          height: calc(100% - 56px);
          object-fit: contain;
          object-position: center;
        }
        .flp-cta {
          align-self: flex-start;
          max-width: 100%;
          white-space: nowrap;
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .flp-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .flp-cta:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .flp-cta:hover::before {
          left: 100%;
        }
        .flp-cta:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        @media (max-width: 639px) {
          .flp-cta {
            padding-left: 1rem;
            padding-right: 1rem;
            letter-spacing: 0.05em;
          }
        }
        @media (max-width: 380px) {
          .flp-cta {
            font-size: 11px;
            letter-spacing: 0.03em;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FlexibleLivingPrograms',
  displayName: 'Flexible Living Programs',
  tokens: {
    'flexiblelivingprograms.card1_title': {
      label: 'Split Payments Title',
      type: 'text',
      defaultValue: 'Split Payments Easily',
    },
    'flexiblelivingprograms.card1_description': {
      label: 'Split Payments Description',
      type: 'richtext',
      defaultValue:
        '<p>Divide rent into two payments aligned with your pay schedule. Stay on top of bills without stretching your budget.</p>',
    },
    'flexiblelivingprograms.card2_title': {
      label: 'Flexible Deposits Title',
      type: 'text',
      defaultValue: 'Flexible Deposits',
    },
    'flexiblelivingprograms.card2_description': {
      label: 'Flexible Deposits Description',
      type: 'richtext',
      defaultValue:
        '<p>Reduce upfront move-in costs with flexible deposit options — including no deposit, Reduced Deposit&trade;, installments, or a traditional full deposit.</p>',
    },
    'flexiblelivingprograms.card3_title': {
      label: 'Insurance Title',
      type: 'text',
      defaultValue: 'Insurance Done Right',
    },
    'flexiblelivingprograms.card3_description': {
      label: 'Insurance Description',
      type: 'richtext',
      defaultValue:
        '<p>Get a quick quote and the liability coverage you need — with optional personal property protection for peace of mind.</p>',
    },
    'flexiblelivingprograms.card4_title': {
      label: 'Rewards Title',
      type: 'text',
      defaultValue: 'Rewards Program',
    },
    'flexiblelivingprograms.card4_description': {
      label: 'Rewards Description',
      type: 'richtext',
      defaultValue:
        '<p>Get rewarded for things you already pay for. Earn points for insurance, redeem them for gift cards and dining, and build credit along the way.</p>',
    },
    'flexiblelivingprograms.card5_title': {
      label: 'Lease Guarantee Title',
      type: 'text',
      defaultValue: 'Lease Guarantee',
    },
    'flexiblelivingprograms.card5_description': {
      label: 'Lease Guarantee Description',
      type: 'richtext',
      defaultValue:
        "<p>Great for the self-employed, first-time renters, or anyone who doesn't quite meet standard requirements. A lease guarantor helps you move in faster.</p>",
    },
    'flexiblelivingprograms.card6_title': {
      label: 'Self-Guided Tours Title',
      type: 'text',
      defaultValue: 'Self-Guided Tours',
    },
    'flexiblelivingprograms.card6_description': {
      label: 'Self-Guided Tours Description',
      type: 'richtext',
      defaultValue:
        "<p>Tour on your schedule with Pineapple's self-guided touring platform. Available evenings and weekends so you can see your future home when it works for you.</p>",
    },
    'flexiblelivingprograms.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue:
        "<p>These programs are provided by independent third-party service providers and are not owned, operated, endorsed, or guaranteed by the property owner or its property management company. Participation is optional and subject to each provider's terms, eligibility, and fees.</p>",
    },
  },
  slotGroups: {
    'flexiblelivingprograms.card1_logo': card1LogoDef,
    'flexiblelivingprograms.card1_cta': card1CtaDef,
    'flexiblelivingprograms.card2_logo': card2LogoDef,
    'flexiblelivingprograms.card2_cta': card2CtaDef,
    'flexiblelivingprograms.card3_logo': card3LogoDef,
    'flexiblelivingprograms.card3_cta': card3CtaDef,
    'flexiblelivingprograms.card4_logo': card4LogoDef,
    'flexiblelivingprograms.card4_cta': card4CtaDef,
    'flexiblelivingprograms.card5_logo': card5LogoDef,
    'flexiblelivingprograms.card5_cta': card5CtaDef,
    'flexiblelivingprograms.card6_logo': card6LogoDef,
    'flexiblelivingprograms.card6_cta': card6CtaDef,
  },
};

(FlexibleLivingPrograms as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FlexibleLivingPrograms;
